import React, { useState } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Users, ReceiptText } from 'lucide-react';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { cn } from '../../../utils/cn';
import { formatCurrency, formatDecimal } from '../../../utils/format';
import type { BusinessReportResponse } from '../../../services/report.service';
import { useLanguage } from '../../../features/language/useLanguage';
import { CARD_PAGE_SIZE, EmptyState, ReportCard, thBase } from './ReportCard';

interface BusinessReportSectionProps {
  report: BusinessReportResponse;
  loading: boolean;
  error: string | null;
  currencySymbol: string;
}

export const BusinessReportSection: React.FC<BusinessReportSectionProps> = ({
  report,
  loading,
  error,
  currencySymbol,
}) => {
  const { t } = useLanguage();
  const [customerPage, setCustomerPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);

  if (loading) {
    return (
      <section className="bg-white border border-gray-200 rounded-xl p-6" data-test-id="business-report-section">
        <LoadingSpinner text={t('reports.business.loading')} />
      </section>
    );
  }

  if (error) {
    return (
      <section className="bg-white border border-gray-200 rounded-xl p-6" data-test-id="business-report-section">
        <p className="text-sm text-red-600">{error}</p>
      </section>
    );
  }

  // Client-side paging: the report payload is already fully loaded. Clamp the
  // page so a shrinking dataset never lands on an empty page.
  const customers = report.customerBreakdown;
  const customerPages = Math.max(1, Math.ceil(customers.length / CARD_PAGE_SIZE));
  const customerSafePage = Math.min(customerPage, customerPages);
  const customerRows = customers.slice((customerSafePage - 1) * CARD_PAGE_SIZE, customerSafePage * CARD_PAGE_SIZE);
  const maxRevenue = customers.reduce((max, row) => Math.max(max, row.totalRevenue), 0);

  const history = report.purchaseHistory;
  const historyPages = Math.max(1, Math.ceil(history.length / CARD_PAGE_SIZE));
  const historySafePage = Math.min(historyPage, historyPages);
  const historyRows = history.slice((historySafePage - 1) * CARD_PAGE_SIZE, historySafePage * CARD_PAGE_SIZE);

  return (
    <section className="space-y-4" data-test-id="business-report-section">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500">{t('reports.business.kpi.totalRevenue')}</p>
          <p className="mt-1 text-xl font-semibold text-gray-900">
            {formatCurrency(report.summary.totalRevenue, currencySymbol)}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500">{t('reports.business.kpi.netProfit')}</p>
          <p className="mt-1 text-xl font-semibold text-blue-600">
            {formatCurrency(report.summary.netProfit, currencySymbol)}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500">{t('reports.business.kpi.totalOrders')}</p>
          <p className="mt-1 text-xl font-semibold text-gray-900">{formatDecimal(report.summary.totalOrders, 0)}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500">{t('reports.business.kpi.activeCustomers')}</p>
          <p className="mt-1 text-xl font-semibold text-gray-900">{formatDecimal(report.summary.activeCustomers, 0)}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500">{t('reports.business.kpi.averageOrderValue')}</p>
          <p className="mt-1 text-xl font-semibold text-gray-900">
            {formatCurrency(report.summary.averageOrderValue, currencySymbol)}
          </p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4" data-test-id="business-trend-card">
        <p className="text-sm font-semibold text-gray-900 mb-3">{t('reports.business.revenueTrend')}</p>
        {report.revenueTrend.length === 0 ? (
          <p className="text-sm text-gray-500">{t('reports.business.emptyTrend')}</p>
        ) : (
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={report.revenueTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(val: string) => {
                    if (val && typeof val === 'string' && val.includes('-')) {
                      const parts = val.split('-');
                      if (parts.length === 3) return `${parts[2]}/${parts[1]}`;
                    }
                    return val;
                  }}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickFormatter={(val: number) => new Intl.NumberFormat(undefined, { notation: 'compact', compactDisplay: 'short' }).format(val)}
                  width={60}
                />
                <Tooltip formatter={(value) => [formatCurrency(Number(value ?? 0), currencySymbol), t('reports.business.kpi.totalRevenue')]} />
                <Area type="monotone" dataKey="revenue" stroke="#2563eb" fill="#93c5fd" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-2">
        {/* Revenue by customer — a leaderboard, so rows carry a rank and a bar
            showing each customer's share of the top earner. */}
        <ReportCard
          icon={<Users className="h-5 w-5" strokeWidth={2} />}
          title={t('reports.business.revenueByCustomer')}
          count={customers.length}
          page={customerSafePage}
          total={customers.length}
          onPageChange={setCustomerPage}
          testId="business-customer-card"
        >
          {customers.length === 0 ? (
            <EmptyState icon={<Users className="h-5 w-5" />} message={t('reports.business.emptyCustomerRevenue')} />
          ) : (
            <table className="min-w-full" data-test-id="business-customer-table">
              <thead className="bg-gray-50/70">
                <tr>
                  <th className={cn(thBase, 'w-10 text-left text-gray-400')}>#</th>
                  <th className={cn(thBase, 'text-left')}>{t('reports.business.table.customer')}</th>
                  <th className={cn(thBase, 'text-right')}>{t('reports.business.table.orders')}</th>
                  <th className={cn(thBase, 'px-4 text-right')}>{t('reports.business.table.revenue')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {customerRows.map((row, i) => {
                  const rank = (customerSafePage - 1) * CARD_PAGE_SIZE + i + 1;
                  const share = maxRevenue > 0 ? row.totalRevenue / maxRevenue : 0;
                  const isTop = rank <= 3;
                  return (
                    <tr
                      key={row.customerId || row.customerName}
                      className="transition-colors hover:bg-primary-50/40"
                      data-test-id={`business-customer-row-${row.customerId || 'unknown'}`}
                    >
                      <td className="px-3 py-2.5">
                        <span
                          className={cn(
                            'flex h-6 w-6 items-center justify-center rounded-md text-xs font-semibold tabular-nums',
                            isTop ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500'
                          )}
                        >
                          {rank}
                        </span>
                      </td>
                      <td className="max-w-[11rem] truncate px-3 py-2.5 text-sm font-medium text-gray-900" title={row.customerName}>
                        {row.customerName}
                      </td>
                      <td className="px-3 py-2.5 text-right text-sm tabular-nums text-gray-600">
                        {formatDecimal(row.orderCount, 0)}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-col items-end gap-1.5">
                          <span className="text-sm font-semibold tabular-nums text-gray-900">
                            {formatCurrency(row.totalRevenue, currencySymbol)}
                          </span>
                          <span className="block h-1 w-full max-w-[6rem] overflow-hidden rounded-full bg-gray-100">
                            <span
                              className={cn('block h-full rounded-full', isTop ? 'bg-primary-500' : 'bg-primary-300')}
                              style={{ width: `${Math.max(share * 100, 3)}%` }}
                            />
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </ReportCard>

        {/* Sales history — a chronological ledger; each row is a real order, so
            the order number leads as a monospaced reference chip. */}
        <ReportCard
          icon={<ReceiptText className="h-5 w-5" strokeWidth={2} />}
          title={t('reports.business.purchaseHistory')}
          count={history.length}
          page={historySafePage}
          total={history.length}
          onPageChange={setHistoryPage}
          testId="business-history-card"
        >
          {history.length === 0 ? (
            <EmptyState icon={<ReceiptText className="h-5 w-5" />} message={t('reports.business.emptyPurchaseHistory')} />
          ) : (
            <table className="min-w-full" data-test-id="business-history-table">
              <thead className="bg-gray-50/70">
                <tr>
                  <th className={cn(thBase, 'px-4 text-left')}>{t('reports.business.table.order')}</th>
                  <th className={cn(thBase, 'text-left')}>{t('reports.business.table.customer')}</th>
                  <th className={cn(thBase, 'px-4 text-right')}>{t('reports.business.table.amount')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {historyRows.map(row => (
                  <tr
                    key={row.orderId}
                    className="transition-colors hover:bg-primary-50/40"
                    data-test-id={`business-order-row-${row.orderId}`}
                  >
                    <td className="px-4 py-2.5">
                      <span className="inline-block rounded bg-gray-50 px-1.5 py-0.5 font-mono text-xs text-gray-500 ring-1 ring-inset ring-gray-200">
                        {row.orderNumber}
                      </span>
                    </td>
                    <td className="max-w-[11rem] truncate px-3 py-2.5 text-sm font-medium text-gray-900" title={row.customerName}>
                      {row.customerName}
                    </td>
                    <td className="px-4 py-2.5 text-right text-sm font-semibold tabular-nums text-gray-900">
                      {formatCurrency(row.totalAmount, currencySymbol)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </ReportCard>
      </div>
    </section>
  );
};
